import React, { useState, useEffect, useContext, useRef } from 'react';
import { Link } from 'react-router-dom';
import { eventAPI } from '../api';
import { AuthContext } from '../AuthContext';
import CarouselDots from '../components/CarouselDots';
import { t } from '../i18n';

const supportedLocales = new Set([
  'ar', 'bg', 'ca', 'cs', 'de', 'el', 'en', 'es', 'fa', 'fi', 'fr', 'he',
  'hr', 'it', 'ja', 'ko', 'lt', 'no', 'pl', 'pt-BR', 'ro', 'ru', 'sk', 'sl',
  'sr', 'sv', 'tr', 'uz', 'vi', 'zh-Hans', 'zh-Hant'
]);

const getCurrentLanguage = () => {
  try {
    return localStorage.getItem('language') || navigator.language || 'en';
  } catch (e) {
    return navigator.language || 'en';
  }
};

const getLocale = () => {
  const language = getCurrentLanguage();
  if (supportedLocales.has(language)) return language;
  const baseLanguage = language.split('-')[0];
  return supportedLocales.has(baseLanguage) ? baseLanguage : 'en';
};

const HomePage = () => {
  const { user } = useContext(AuthContext);
  const [events, setEvents] = useState([]);
  const [currentIndex, setCurrentIndex] = useState(0);
  const [isAnimating, setIsAnimating] = useState(false);
  const animTimeoutRef = useRef(null);
  const viewportRef = useRef(null);
  const trackRef = useRef(null);
  const dotContainerRef = useRef(null);
  const scrollTimeoutRef = useRef(null);
  const touchStartRef = useRef(null);
  const GAP_PX = 16; // must match CSS .carousel-track gap

  useEffect(() => {
    loadUnansweredEvents();
    return () => {
      if (animTimeoutRef.current) clearTimeout(animTimeoutRef.current);
      if (scrollTimeoutRef.current) clearTimeout(scrollTimeoutRef.current);
    };
  }, [user]);

  // scroll to index when user interacts with dots or when index changes externally
  useEffect(() => {
    const vp = viewportRef.current;
    const track = trackRef.current;
    if (!vp || !track) return;
    const child = track.children[currentIndex];
    if (!child) return;
    const targetLeft = child.offsetLeft + child.offsetWidth / 2 - vp.clientWidth / 2;
    vp.scrollTo({ left: targetLeft, behavior: 'smooth' });

    // ensure active dot is visible/centered
    const dots = dotContainerRef.current;
    if (dots && dots.children && dots.children[currentIndex]) {
      try {
        dots.children[currentIndex].scrollIntoView({ inline: 'center', behavior: 'smooth' });
      } catch (e) {
        // ignore older browsers
      }
    }
  }, [currentIndex, events.length]);

  const loadUnansweredEvents = async () => {
    if (!user) return;
    try {
      // Request no upper bound (all upcoming events) using the server-supported 'all' flag
      const resp = await eventAPI.getUpcoming('all');
      if (!Array.isArray(resp.data)) return setEvents([]);

      const now = new Date();
      const ONE_HOUR_MS = 60 * 60 * 1000;

      // Filter: exclude user's own hosted copies; include events starting within next day/week but
      // we only want those the user has NOT answered yet (no my_response and no response in list)
      const candidates = resp.data.filter(ev => {
        if (!ev) return false;
        if (ev.creator && user && ev.creator.id === user.id) return false;
        // prefer server-provided `my_response` when available
        if (ev.my_response !== undefined && ev.my_response !== null) {
          return ev.my_response === null;
        }
        // fallback: examine responses array if present
        if (Array.isArray(ev.responses)) {
          const mine = ev.responses.find(r => r.user && r.user.id === (user && user.id));
          return !mine;
        }
        // conservative: include if we don't know
        return true;
      });

      // sort chronologically
      candidates.sort((a, b) => new Date(a.date_time) - new Date(b.date_time));
      setEvents(candidates);
      setCurrentIndex(0);
    } catch (err) {
      // keep quiet on errors (no console spam in production)
    }
  };

  const handleRespond = async (copyId, response) => {
    if (!events || events.length === 0) return;
    const idx = currentIndex;
    try {
      await eventAPI.respond(copyId, response);
    } catch (err) {
      // ignore network errors for now
    }

    // swipe/scroll-based slide: if there is a next item, scroll to it then remove the answered one
    const vp = viewportRef.current;
    if (events.length > 1 && idx < events.length - 1 && vp) {
      setIsAnimating(true);
      const cardFull = Math.round(vp.clientWidth * 0.84) + GAP_PX;
      const nextIndex = idx + 1;
      vp.scrollTo({ left: nextIndex * cardFull, behavior: 'smooth' });

      animTimeoutRef.current = setTimeout(() => {
        setEvents(prev => {
          const copy = [...prev];
          copy.splice(idx, 1); // remove the answered item
          return copy;
        });
        // after removal, keep visual index pointing to the same slot (which now contains the next item)
        // adjust scroll position instantly to align
        requestAnimationFrame(() => {
          const afterCardFull = Math.round(vp.clientWidth * 0.84) + GAP_PX;
          vp.scrollLeft = idx * afterCardFull;
        });
        setIsAnimating(false);
        setCurrentIndex(idx);
      }, 360);
    } else {
      // last item or single item: remove it and reset index
      setEvents(prev => {
        const copy = [...prev];
        copy.splice(idx, 1);
        return copy;
      });
      setCurrentIndex(0);
      if (viewportRef.current) viewportRef.current.scrollLeft = 0;
    }
  };

  if (!events || events.length === 0) {
    return (
      <div className="container">
        <div className="card" style={{ textAlign: 'center', padding: '50px' }}>
          <h2>{t('noEventsToRespondTo')}</h2>
          <p>{t('allCaughtUp')}</p>
        </div>
      </div>
    );
  }

  // use scroll-snap / native swipe. track current index from scroll position
  const onViewportScroll = () => {
    if (scrollTimeoutRef.current) clearTimeout(scrollTimeoutRef.current);
    scrollTimeoutRef.current = setTimeout(() => {
      const vp = viewportRef.current;
      const track = trackRef.current;
      if (!vp || !track) return;
      const vpCenter = vp.scrollLeft + vp.clientWidth / 2;
      let bestIdx = 0;
      let bestDist = Infinity;
      const children = track.children;
      for (let i = 0; i < children.length; i++) {
        const child = children[i];
        const childCenter = child.offsetLeft + child.offsetWidth / 2;
        const d = Math.abs(childCenter - vpCenter);
        if (d < bestDist) {
          bestDist = d;
          bestIdx = i;
        }
      }
      setCurrentIndex(Math.max(0, Math.min(events.length - 1, bestIdx)));
    }, 80);
  };

  const onTouchStart = (e) => {
    if (e.touches && e.touches[0]) {
      touchStartRef.current = { x: e.touches[0].clientX, time: Date.now() };
    }
  };

  const onTouchEnd = (e) => {
    const start = touchStartRef.current;
    if (!start || !viewportRef.current) return;
    const endX = (e.changedTouches && e.changedTouches[0]) ? e.changedTouches[0].clientX : null;
    if (endX === null) { touchStartRef.current = null; return; }
    const dx = start.x - endX; // positive if swiped left (to next)
    const dt = Math.max(1, Date.now() - start.time);
    const absDx = Math.abs(dx);
    const velocity = absDx / dt; // px per ms

    const vp = viewportRef.current;
    const track = trackRef.current;
    const children = track ? track.children : null;
    const swipeThreshold = Math.max(28, vp.clientWidth * 0.06); // sensible threshold
    const velocityThreshold = 0.25; // quick flick

    if (absDx > swipeThreshold || velocity > velocityThreshold) {
      if (dx > 0) {
        // move forward one
        const target = Math.min(events.length - 1, currentIndex + 1);
        setCurrentIndex(target);
      } else {
        // move backward one
        const target = Math.max(0, currentIndex - 1);
        setCurrentIndex(target);
      }
    } else {
      // snap to nearest child based on center
      if (vp && track && children && children.length) {
        const vpCenter = vp.scrollLeft + vp.clientWidth / 2;
        let bestIdx = 0; let bestDist = Infinity;
        for (let i = 0; i < children.length; i++) {
          const childCenter = children[i].offsetLeft + children[i].offsetWidth / 2;
          const d = Math.abs(childCenter - vpCenter);
          if (d < bestDist) { bestDist = d; bestIdx = i; }
        }
        setCurrentIndex(Math.max(0, Math.min(events.length - 1, bestIdx)));
      }
    }

    touchStartRef.current = null;
  };

  const handleCardClick = (e, idx) => {
    // Ignore while animating/removing
    if (isAnimating) return;

    // If the user clicked an interactive element inside the card (link, button, input), don't hijack it
    try {
      const el = e.target && e.target.closest ? e.target.closest('a, button, input, textarea, select, label') : null;
      if (el) return;
    } catch (err) {
      // ignore DOM lookup errors
    }

    if (idx === currentIndex) return;
    setCurrentIndex(idx);
  };
  return (
    <div className="container">
      <h1>{t('events')}</h1>
      <div className="card event-carousel">
        <div className="carousel-viewport" ref={viewportRef} onScroll={onViewportScroll} onTouchStart={onTouchStart} onTouchEnd={onTouchEnd}>
          <div className="carousel-track" ref={trackRef}>
            {events.map((ev, i) => (
              <div className="carousel-card" key={ev.copy_id} onClick={(e) => handleCardClick(e, i)}>
                <div className="carousel-inner">
                  <h2 style={{ marginTop: 0 }}>{ev.title}</h2>
                  <div style={{ color: '#666', marginBottom: 8 }}>
                    {(() => {
                      const d = new Date(ev.date_time);
                      const date = d.toLocaleDateString(getLocale(), { day: '2-digit', month: '2-digit', year: 'numeric' });
                      const time = d.toLocaleTimeString(getLocale(), { hour: '2-digit', minute: '2-digit' });
                      return `${date} ${time}`;
                    })()}
                  </div>
                  {/* Hosts/owners */}
                  <div style={{ color: '#444', marginBottom: 8 }}>
                    <strong>{t('hosts')}:</strong>{' '}
                    {(ev.hosts && ev.hosts.length > 0) ? (
                      ev.hosts.map((h, hi) => (
                        <span key={h}>
                          <Link to={`/users/${encodeURIComponent(h)}`} className="text-link">{h}</Link>{hi < ev.hosts.length - 1 ? ', ' : ''}
                        </span>
                      ))
                    ) : (
                      ev.creator ? <Link to={`/users/${encodeURIComponent(ev.creator.username)}`} className="text-link">{ev.creator.username}</Link> : t('unknown')
                    )}
                  </div>
                  {ev.place_name ? <div style={{ fontWeight: 700, marginBottom: 8 }}>{ev.place_name}</div> : null}
                  {ev.description ? <p style={{ color: '#333' }}>{ev.description}</p> : <p style={{ color: '#666' }}>{t('noDescriptionProvided')}</p>}

                  <div className="response-buttons" style={{ marginTop: 16 }}>
                    <button className="btn btn-success" onClick={() => handleRespond(ev.copy_id, 'will')}>{t('will')}</button>
                    <button className="btn btn-might" onClick={() => handleRespond(ev.copy_id, 'might')}>{t('maybe')}</button>
                    <button className="btn btn-danger" onClick={() => handleRespond(ev.copy_id, 'wont')}>{t('wont')}</button>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>

        {/* Dots */}
        <div className="carousel-controls" style={{ marginTop: 12 }}>
          <CarouselDots 
            count={events.length} 
            currentIndex={currentIndex} 
            onSelect={setCurrentIndex} 
          />
        </div>
      </div>
    </div>
  );
};

export default HomePage;
